import { recomputeLoopNormals, resolveAutoSmoothAngle } from "./meshOps.ts";
import {
  canonicalizeTopology,
  triangulateTopologyForRendering,
} from "./topology.ts";
import type {
  LoopNormals,
  PolygonTopology,
  RenderTriangleIndices,
  SceneObject,
  ShadingMode,
} from "./types.ts";
import { asLoopNormals, isEmptyObject, isLightObject } from "./types.ts";
import { unwrapBoxProject } from "./uvUnwrap.ts";

export interface NormalizedTopologyBackedGeometry {
  topology: PolygonTopology;
  indices: RenderTriangleIndices;
  loopNormals: LoopNormals;
  uvs?: Float32Array;
}

interface NormalizeTopologyBackedGeometryOptions {
  loopNormals?: Float32Array | LoopNormals | null;
  uvs?: Float32Array | null;
  shading?: ShadingMode;
  autoSmoothAngle?: number;
  generateMissingUvs?: boolean;
}

function assertNormalizableTopologyBackedGeometry(
  vertices: Float32Array,
  topology: PolygonTopology,
) {
  if (vertices.length % 3 !== 0) {
    throw new Error(
      `Topology-backed geometry: vertices.length ${vertices.length} is not divisible by 3`,
    );
  }
  if (topology.offsets.length === 0) {
    throw new Error("Topology-backed geometry: topology.offsets is empty");
  }
  if (topology.offsets[0] !== 0) {
    throw new Error(
      `Topology-backed geometry: topology.offsets[0] must be 0, got ${topology.offsets[0]}`,
    );
  }
  if (
    topology.offsets[topology.offsets.length - 1] !== topology.corners.length
  ) {
    throw new Error(
      `Topology-backed geometry: topology.offsets[last] = ${topology.offsets[topology.offsets.length - 1]} ` +
        `does not equal topology.corners.length ${topology.corners.length}`,
    );
  }

  const vertexCount = vertices.length / 3;
  for (let i = 0; i < topology.corners.length; i++) {
    if (topology.corners[i] >= vertexCount) {
      throw new Error(
        `Topology-backed geometry: topology.corners[${i}] = ${topology.corners[i]} ` +
          `>= vertexCount ${vertexCount}`,
      );
    }
  }
}

function expandPerVertexNormalsToCorners(
  topology: PolygonTopology,
  vertexNormals: Float32Array,
): LoopNormals {
  const { corners } = topology;
  const loopNormals = new Float32Array(corners.length * 3);
  for (let c = 0; c < corners.length; c++) {
    const vi = corners[c];
    loopNormals[c * 3] = vertexNormals[vi * 3];
    loopNormals[c * 3 + 1] = vertexNormals[vi * 3 + 1];
    loopNormals[c * 3 + 2] = vertexNormals[vi * 3 + 2];
  }
  return asLoopNormals(loopNormals);
}

function expandPerVertexUvsToCorners(
  topology: PolygonTopology,
  perVertexUvs: Float32Array,
): Float32Array {
  const { corners } = topology;
  const loopUvs = new Float32Array(corners.length * 2);
  for (let c = 0; c < corners.length; c++) {
    const vi = corners[c];
    loopUvs[c * 2] = perVertexUvs[vi * 2];
    loopUvs[c * 2 + 1] = perVertexUvs[vi * 2 + 1];
  }
  return loopUvs;
}

function normalizeLoopNormals(
  vertices: Float32Array,
  topology: PolygonTopology,
  loopNormals: Float32Array | LoopNormals | null | undefined,
  shading: ShadingMode,
  autoSmoothAngle?: number,
): LoopNormals {
  const cornerCount = topology.corners.length;
  const vertexCount = vertices.length / 3;

  if (loopNormals && loopNormals.length === cornerCount * 3) {
    return asLoopNormals(new Float32Array(loopNormals));
  }

  if (loopNormals && loopNormals.length === vertexCount * 3) {
    return expandPerVertexNormalsToCorners(topology, loopNormals);
  }

  return recomputeLoopNormals(
    vertices,
    topology,
    resolveAutoSmoothAngle(shading, autoSmoothAngle),
  );
}

function normalizeLoopUvs(
  vertices: Float32Array,
  topology: PolygonTopology,
  loopNormals: LoopNormals,
  uvs: Float32Array | null | undefined,
  generateMissingUvs: boolean,
): Float32Array | undefined {
  const cornerCount = topology.corners.length;
  const vertexCount = vertices.length / 3;

  if (uvs && uvs.length === cornerCount * 2) {
    return new Float32Array(uvs);
  }

  if (uvs && uvs.length === vertexCount * 2) {
    return expandPerVertexUvsToCorners(topology, uvs);
  }

  if (!generateMissingUvs) return undefined;
  return unwrapBoxProject(vertices, loopNormals, topology);
}

export function normalizeTopologyBackedGeometry(
  vertices: Float32Array,
  topology: PolygonTopology,
  opts: NormalizeTopologyBackedGeometryOptions = {},
): NormalizedTopologyBackedGeometry {
  assertNormalizableTopologyBackedGeometry(vertices, topology);
  const normalizedTopology = canonicalizeTopology(topology);
  const indices = triangulateTopologyForRendering(normalizedTopology);
  const shading = opts.shading ?? "smooth";
  const loopNormals = normalizeLoopNormals(
    vertices,
    normalizedTopology,
    opts.loopNormals,
    shading,
    opts.autoSmoothAngle,
  );
  const uvs = normalizeLoopUvs(
    vertices,
    normalizedTopology,
    loopNormals,
    opts.uvs,
    opts.generateMissingUvs ?? true,
  );
  return {
    topology: normalizedTopology,
    indices,
    loopNormals,
    uvs,
  };
}

export function normalizeSceneObjectGeometry(obj: SceneObject): SceneObject {
  if (isLightObject(obj) || isEmptyObject(obj)) return obj;
  if (obj.vertices.length === 0 && obj.topology.corners.length === 0)
    return obj;

  const normalized = normalizeTopologyBackedGeometry(
    obj.vertices,
    obj.topology,
    {
      loopNormals: obj.loopNormals,
      uvs: obj.uvs,
      shading: obj.shading,
      autoSmoothAngle: obj.autoSmoothAngle,
      generateMissingUvs: true,
    },
  );

  return {
    ...obj,
    topology: normalized.topology,
    indices: normalized.indices,
    loopNormals: normalized.loopNormals,
    uvs: normalized.uvs,
  };
}
